import { sum } from 'lodash-es'
import { useMemo } from 'react'

import { getBreakdownLine, OptionalCover, Quote } from '../domain'
import { PaymentScheduleType, ProductConfig } from '../state/configuration'
import { useCoverLevelOption } from './coverOption'
import { useOptionalCovers } from './covers'

export interface QuotePremiums {
  paymentSchedule: PaymentScheduleType | undefined

  base: {
    premium: number
    discount: number
  }

  breakdown: {
    base: Record<string, number>
    additional: Record<string, number>
  }

  totals: {
    gross: number
    term: number
    discounted: number
    taxAmount: number
  }

  optional: OptionalCover[]
}

export const useQuotePremiums = (
  quote: Quote | undefined,
  product: ProductConfig,
): QuotePremiums => {
  const optionalCovers = useOptionalCovers(quote, product)
  const { coverLevelOption, coverLevelConfig } = useCoverLevelOption(quote, product)

  return useMemo(() => {
    const optionalActive = optionalCovers.filter((it) => it.active)
    const basePremiums = coverLevelOption?.basePremiumItems ?? []

    const baseDiscount = coverLevelOption?.promotion?.discountAmt ?? 0
    const basePremium = sum(basePremiums.map((it) => it.premium))
    const baseTotalPremium = sum(basePremiums.map((it) => it.totalPremium))

    const optionalCoverPremium = sum(optionalActive.map((it) => it.premium))
    const optionalCoverTotal = sum(optionalActive.map((it) => it.totalPremium))

    const taxAmountBase = sum(basePremiums.map((it) => it.taxAmount))
    const taxAmountOptional = sum(optionalActive.map((it) => it.taxAmount))

    const totalGross = basePremium + optionalCoverPremium
    const totalTerm = baseTotalPremium + optionalCoverTotal
    const totalDiscounted = totalGross - baseDiscount
    const totalTax = taxAmountBase + taxAmountOptional

    const breakdownLinesBase = basePremiums.flatMap((it) => it.premiumBreakdownLines)

    const breakdownLinesAdditional = [
      ...optionalActive.flatMap((it) => it.additionalPremiumBreakdownLines),
      ...basePremiums.flatMap((it) => it.additionalPremiumBreakdownLines),
    ]

    const breakdownBase = getBreakdownLine(
      breakdownLinesBase,
      coverLevelConfig?.pricing?.base,
    )

    const breakdownAdditional = getBreakdownLine(
      breakdownLinesAdditional,
      coverLevelConfig?.pricing?.additional,
    )

    return {
      paymentSchedule: coverLevelOption?.paymentScheduleType,
      optional: optionalActive,

      base: {
        discount: baseDiscount,
        premium: basePremium,
      },

      breakdown: {
        base: breakdownBase,
        additional: breakdownAdditional,
      },

      totals: {
        gross: totalGross,
        term: totalTerm,
        discounted: totalDiscounted,
        taxAmount: totalTax,
      },
    } satisfies QuotePremiums
  }, [optionalCovers, coverLevelOption, coverLevelConfig])
}
